import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import DataSourcesPanel from '../components/DataSourcesPanel';
import DepartmentsPanel from '../components/DepartmentsPanel';
import EmailConfigPanel from '../components/EmailConfigPanel';
import TaskStatusesPanel from '../components/TaskStatusesPanel';

export default function TenantSettings({ onClose }) {
  const { user } = useAuth();
  const { activeTenant, tenantFetch, loadTenants } = useTenant();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [identifier, setIdentifier] = useState('');
  const [newRole, setNewRole] = useState('member');
  const [tenantName, setTenantName] = useState(activeTenant?.name || '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const myRole = activeTenant?.role;
  const isAdmin = myRole === 'admin' || myRole === 'owner';
  const isOwner = myRole === 'owner';
  const canManageStatuses = user?.role === 'admin' || !!user?.canManageTaskStatuses;

  const loadMembers = useCallback(async () => {
    if (!activeTenant) return;
    setLoading(true);
    try {
      const res = await tenantFetch(`/api/tenants/${activeTenant._id}/members`);
      const data = await res.json();
      if (res.ok) setMembers(data.members || []);
    } finally {
      setLoading(false);
    }
  }, [activeTenant, tenantFetch]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    setError('');
    setBusy(true);
    try {
      const res = await tenantFetch(`/api/tenants/${activeTenant._id}/members`, {
        method: 'POST',
        body: JSON.stringify({ identifier: identifier.trim(), role: newRole })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      setIdentifier('');
      setNewRole('member');
      await loadMembers();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      const res = await tenantFetch(`/api/tenants/${activeTenant._id}/members/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      await loadMembers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm('Mitglied wirklich entfernen?')) return;
    try {
      const res = await tenantFetch(`/api/tenants/${activeTenant._id}/members/${userId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      await loadMembers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRename = async (e) => {
    e.preventDefault();
    if (!tenantName.trim() || tenantName.trim() === activeTenant.name) return;
    try {
      const res = await tenantFetch(`/api/tenants/${activeTenant._id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: tenantName.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      await loadTenants();
    } catch (err) {
      alert(err.message);
    }
  };

  if (!activeTenant) return null;

  return (
    <div className="tenant-settings-overlay" onClick={onClose}>
      <div className="tenant-settings-modal" onClick={e => e.stopPropagation()}>
        <div className="tenant-settings-header">
          <h2>Mandanten-Einstellungen</h2>
          <button className="icon-btn" onClick={onClose}>✕</button>
        </div>

        <div className="tenant-settings-body">
          <section className="tenant-settings-section">
            <h3>Allgemein</h3>
            <form onSubmit={handleRename} className="tenant-rename-form">
              <label>Name</label>
              <div className="tenant-rename-row">
                <input
                  className="text-input"
                  value={tenantName}
                  onChange={e => setTenantName(e.target.value)}
                  disabled={!isAdmin}
                />
                <button type="submit" className="primary-btn" disabled={!isAdmin}>Speichern</button>
              </div>
            </form>
          </section>

          <section className="tenant-settings-section">
            <h3>Mitglieder ({members.length})</h3>
            {loading ? (
              <p>Lädt…</p>
            ) : (
              <table className="members-table">
                <thead>
                  <tr>
                    <th>Benutzer</th>
                    <th>E-Mail</th>
                    <th>Rolle</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => (
                    <tr key={m.userId}>
                      <td>
                        {m.username}
                        {m.userId === user._id && <span className="tag-self"> (du)</span>}
                      </td>
                      <td>{m.email}</td>
                      <td>
                        {isOwner && m.userId !== user._id ? (
                          <select
                            value={m.role}
                            onChange={e => handleRoleChange(m.userId, e.target.value)}
                          >
                            <option value="member">Mitglied</option>
                            <option value="admin">Admin</option>
                            <option value="owner">Eigentümer</option>
                          </select>
                        ) : (
                          <span className={`tenant-role-badge role-${m.role}`}>{m.role}</span>
                        )}
                      </td>
                      <td>
                        {(isAdmin || m.userId === user._id) && (
                          <button
                            className="link-btn danger"
                            onClick={() => handleRemove(m.userId)}
                          >
                            {m.userId === user._id ? 'Verlassen' : 'Entfernen'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {isAdmin && (
              <form onSubmit={handleAddMember} className="add-member-form">
                <input
                  className="text-input"
                  placeholder="Benutzername oder E-Mail"
                  value={identifier}
                  onChange={e => setIdentifier(e.target.value)}
                  disabled={busy}
                />
                <select value={newRole} onChange={e => setNewRole(e.target.value)} disabled={busy}>
                  <option value="member">Mitglied</option>
                  <option value="admin">Admin</option>
                </select>
                <button type="submit" className="primary-btn" disabled={busy || !identifier.trim()}>
                  Hinzufügen
                </button>
                {error && <div className="form-error">{error}</div>}
              </form>
            )}
          </section>

          <section className="tenant-settings-section">
            <h3>Abteilungen</h3>
            <p className="section-hint">
              Strukturiere den Mandanten in Abteilungen (z.B. IT, Marketing) und Unter-Abteilungen
              (z.B. IT → Infrastruktur, Softwareentwicklung). Mitglieder können in mehreren Abteilungen sein.
              Leads dürfen alle Aufgaben ihrer Abteilung — und ihrer Unter-Abteilungen — sehen.
            </p>
            <DepartmentsPanel canEdit={isAdmin} />
          </section>

          <section className="tenant-settings-section">
            <h3>Aufgaben-Status</h3>
            <p className="section-hint">
              Definiere die Status, die Aufgaben in diesem Mandanten haben können. Reihenfolge per ▲/▼ ändern.
              „Initial" = neue Aufgaben starten mit diesem Status. „Final" = Aufgabe gilt als abgeschlossen.
              {!canManageStatuses && ' Du hast nur Lese-Rechte — bitte einen Admin um das Recht „Aufgaben-Status verwalten".'}
            </p>
            <TaskStatusesPanel canEdit={canManageStatuses} />
          </section>

          <section className="tenant-settings-section">
            <h3>Datenquellen</h3>
            <p className="section-hint">
              Verbinde SQL-Datenbanken, REST-APIs, SharePoint oder Webhooks. Zugangsdaten werden verschlüsselt gespeichert.
            </p>
            <DataSourcesPanel canEdit={isAdmin} />
          </section>

          <section className="tenant-settings-section">
            <h3>E-Mail-Einstellungen</h3>
            <p className="section-hint">
              SMTP-Konfiguration für den E-Mail-Versand aus Workflows („E-Mail senden"-Schritt). Passwörter
              werden verschlüsselt gespeichert und niemals an den Browser zurückgeschickt — nur ein
              gemaskter Hinweis.
            </p>
            <EmailConfigPanel canEdit={isAdmin} />
          </section>
        </div>
      </div>
    </div>
  );
}
